import type React from 'react';
import { findIndex } from 'lodash-es';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';
import { useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { Box, ProgressStepper } from '@zillow/constellation-10';
import { LoadingText } from '@zg-rentals/loading-components';

import { Section as SectionModel } from '../../shared/models';
import { useCompletionStatusQuery, useGetFirstNodeInSectionCategoryMutation } from '../../shared/utils/queries';
import { putNodeIdInSearch } from '../../shared/utils/urlUtils';
import { useSectionContext } from '../../shared/contexts/SectionContext';

export interface CategoryCompletion {
  categoryName: string;
  displayName: string;
  visited: boolean;
  completed: boolean;
}

function LandlordProgressStepper(): JSX.Element | null {
  const { leaseId } = useLeasingUrlParams();
  const { section } = useSectionContext();
  const history = useHistory();

  const completionStatusQuery = useCompletionStatusQuery(Number(leaseId));
  const completionStatus = completionStatusQuery.data;
  const isLoading = completionStatusQuery.isPending || completionStatusQuery.isLoading;

  const getFirstNodeInSectionCategoryMutation = useGetFirstNodeInSectionCategoryMutation();

  const lastValidActiveIndexRef = useRef<number>(-1);

  async function handleStepClick(sectionCategory: string): Promise<void> {
    const response = await getFirstNodeInSectionCategoryMutation.mutateAsync({
      leaseId: Number(leaseId),
      sectionCategory,
    });
    const newSection = new SectionModel(response);

    if (newSection.currentNodeId) {
      putNodeIdInSearch(`${newSection.currentNodeId}`, history.push);
    }
  }

  const activeIndex =
    completionStatus && section != null
      ? findIndex(
          completionStatus.categoryCompletion,
          (category: CategoryCompletion) => category.categoryName === section.sectionCategory,
        )
      : -1;

  if (activeIndex >= 0) {
    lastValidActiveIndexRef.current = activeIndex;
  }

  const displayActiveIndex = activeIndex >= 0 ? activeIndex : lastValidActiveIndexRef.current;

  // Show loading until we have data and a valid display index
  if (isLoading || !completionStatus || (displayActiveIndex < 0 && lastValidActiveIndexRef.current < 0)) {
    return (
      <Box css={{ marginLeft: 'looser', marginRight: 'looser' }}>
        <LoadingText isLoading loadingWidth="100%">
          Loading
        </LoadingText>
        <Box css={{ display: 'flex', justifyContent: 'space-between', gap: 'looser', marginTop: 'tighter' }}>
          {[1, 2, 3, 4, 5].map((index) => (
            <LoadingText key={index} isLoading loadingWidth="100%">
              Loading
            </LoadingText>
          ))}
        </Box>
      </Box>
    );
  }

  function renderProgressStepper(): React.ReactNode {
    const progressStepperSteps = [];

    completionStatus.categoryCompletion.forEach((category) => {
      if (category.visited) {
        progressStepperSteps.push({
          label: category.displayName,
          appearance: category.completed ? 'complete' : 'incomplete',
          interactive: true,
        });
      } else {
        progressStepperSteps.push({
          label: category.displayName,
          interactive: true,
        });
      }
    });

    return (
      progressStepperSteps.length > 0 &&
      displayActiveIndex >= 0 && (
        <ProgressStepper
          steps={progressStepperSteps}
          activeStepIndex={displayActiveIndex}
          onStepClick={(e, activeStepIndex) => {
            handleStepClick(completionStatus.categoryCompletion[activeStepIndex].categoryName);
          }}
        />
      )
    );
  }

  return <>{renderProgressStepper()}</>;
}

export default LandlordProgressStepper;
